import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search,
  Filter,
  Plus,
  X,
  Loader2,
  Megaphone,
  Clock,
  AlertTriangle,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { formatDateShort } from "@/lib/utils";
import { adminFetch } from "@/lib/api";
import { useFetch } from "@/hooks/use-fetch";
import { PageLoading } from "@/components/loading";
import { ErrorMessage } from "@/components/error-message";
import { Pagination } from "@/components/pagination";
import { MultiSelectTags } from "@/components/multi-select-tags";
import { DatePickerField } from "@/components/date-picker-field";
import {
  PROGRAM_LABELS,
  PROGRAM_INSTITUTIONS,
  SECTORS,
  SUPPORT_RATE_OPTIONS,
} from "@/lib/constants";

// ─── Types ──────────────────────────────────────

interface CallPeriod {
  id: number;
  program_type: string;
  title: string;
  call_number: string;
  institution: string;
  description: string;
  application_start: string;
  application_end: string;
  submission_deadline: string;
  support_rate: string;
  budget: string;
  eligible_cities: string;
  eligible_sectors: string;
  notes: string;
  source_url: string;
  status: string;
  project_count: number;
  created_at: string;
}

interface CallPeriodsResponse {
  data: CallPeriod[];
  pagination: {
    page: number;
    totalPages: number;
    total: number;
  };
}

interface Il {
  id: number;
  name: string;
  bolge: number;
}

// ─── Constants ──────────────────────────────────

const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  draft: {
    label: "Taslak",
    color: "text-yellow-400 bg-yellow-500/10 border-yellow-500/20",
  },
  active: {
    label: "Aktif",
    color: "text-green-400 bg-green-500/10 border-green-500/20",
  },
  closed: {
    label: "Kapandı",
    color: "text-gray-400 bg-gray-500/10 border-gray-500/20",
  },
  upcoming: {
    label: "Yaklaşan",
    color: "text-blue-400 bg-blue-500/10 border-blue-500/20",
  },
};

const STATUS_CYCLE: Record<string, string> = {
  draft: "active",
  active: "closed",
  closed: "active",
};

const statusFilterOptions = [
  { value: "", label: "Tüm Durumlar" },
  { value: "draft", label: "Taslak" },
  { value: "active", label: "Aktif" },
  { value: "closed", label: "Kapandı" },
  { value: "upcoming", label: "Yaklaşan" },
];

const programFilterOptions = [
  { value: "", label: "Tüm Programlar" },
  ...Object.entries(PROGRAM_LABELS).map(([value, label]) => ({
    value,
    label,
  })),
];

const SECTOR_OPTIONS = SECTORS.map((s, i) => ({ id: i + 1, name: s }));

// ─── Helpers ────────────────────────────────────

function getDaysUntilDeadline(deadline: string): number | null {
  if (!deadline) return null;
  const now = new Date();
  const deadlineDate = new Date(deadline);
  const diffMs = deadlineDate.getTime() - now.getTime();
  return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
}

function DeadlineBadge({ deadline }: { deadline: string }) {
  const days = getDaysUntilDeadline(deadline);
  if (days === null) return null;

  if (days < 0) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-red-500/20 bg-red-500/10 px-2 py-0.5 text-xs font-medium text-red-400">
        <AlertTriangle className="h-3 w-3" />
        Süre doldu
      </span>
    );
  }

  if (days <= 30) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-amber-500/20 bg-amber-500/10 px-2 py-0.5 text-xs font-medium text-amber-400">
        <Clock className="h-3 w-3" />
        {days} gün kaldı
      </span>
    );
  }

  return null;
}

function formatBudgetInput(value: string): string {
  const digits = value.replace(/\D/g, "");
  if (!digits) return "";
  return Number(digits).toLocaleString("tr-TR");
}

function parseDateToInputValue(date: Date | null): string {
  if (!date) return "";
  try {
    return date.toISOString().split("T")[0];
  } catch {
    return "";
  }
}

// ─── Main Component ─────────────────────────────

export function CallPeriodsPage() {
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("");
  const [programFilter, setProgramFilter] = useState("");
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [changingStatusId, setChangingStatusId] = useState<number | null>(null);
  const navigate = useNavigate();

  // Form state
  const [formProgramType, setFormProgramType] = useState("");
  const [formTitle, setFormTitle] = useState("");
  const [formCallNumber, setFormCallNumber] = useState("");
  const [formDescription, setFormDescription] = useState("");
  const [formApplicationStart, setFormApplicationStart] =
    useState<Date | null>(null);
  const [formApplicationEnd, setFormApplicationEnd] = useState<Date | null>(
    null
  );
  const [formSubmissionDeadline, setFormSubmissionDeadline] =
    useState<Date | null>(null);
  const [formSupportRate, setFormSupportRate] = useState("");
  const [formBudget, setFormBudget] = useState("");
  const [formSelectedCities, setFormSelectedCities] = useState<
    { id: number; name: string }[]
  >([]);
  const [formSelectedSectors, setFormSelectedSectors] = useState<
    { id: number; name: string }[]
  >([]);
  const [formNotes, setFormNotes] = useState("");
  const [formSourceUrl, setFormSourceUrl] = useState("");

  // City data
  const [iller, setIller] = useState<{ id: number; name: string }[]>([]);
  const [citiesLoading, setCitiesLoading] = useState(false);

  const derivedInstitution = formProgramType
    ? PROGRAM_INSTITUTIONS[formProgramType] || ""
    : "";

  const queryParams = new URLSearchParams();
  queryParams.set("page", String(page));
  queryParams.set("limit", "20");
  if (statusFilter) queryParams.set("status", statusFilter);
  if (programFilter) queryParams.set("program_type", programFilter);
  if (search) queryParams.set("q", search);

  const { data, loading, error, refetch } = useFetch<CallPeriodsResponse>(
    `/call-periods?${queryParams.toString()}`,
    [page, statusFilter, programFilter, search]
  );

  // Fetch cities when modal opens
  useEffect(() => {
    if (!showModal) return;
    setCitiesLoading(true);
    const API_BASE = import.meta.env.VITE_API_URL
      ? `${import.meta.env.VITE_API_URL}/api`
      : "/api";
    fetch(`${API_BASE}/il`)
      .then((r) => r.json())
      .then((json) => {
        const items: Il[] = Array.isArray(json.data) ? json.data : [];
        setIller(
          items
            .sort((a, b) => a.name.localeCompare(b.name, "tr"))
            .map((il) => ({ id: il.id, name: il.name }))
        );
      })
      .catch(() => {})
      .finally(() => setCitiesLoading(false));
  }, [showModal]);

  function resetForm() {
    setFormProgramType("");
    setFormTitle("");
    setFormCallNumber("");
    setFormDescription("");
    setFormApplicationStart(null);
    setFormApplicationEnd(null);
    setFormSubmissionDeadline(null);
    setFormSupportRate("");
    setFormBudget("");
    setFormSelectedCities([]);
    setFormSelectedSectors([]);
    setFormNotes("");
    setFormSourceUrl("");
  }

  async function handleStatusCycle(cpId: number, currentStatus: string) {
    const nextStatus = STATUS_CYCLE[currentStatus];
    if (!nextStatus) return;
    setChangingStatusId(cpId);
    try {
      await adminFetch(`/call-periods/${cpId}`, {
        method: "PATCH",
        body: JSON.stringify({ status: nextStatus }),
      });
      refetch();
    } catch {
      // Hata
    } finally {
      setChangingStatusId(null);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!formProgramType || !formTitle.trim()) return;
    setSubmitting(true);
    try {
      const payload = {
        program_type: formProgramType,
        title: formTitle,
        call_number: formCallNumber || undefined,
        institution: derivedInstitution || undefined,
        description: formDescription || undefined,
        application_start:
          parseDateToInputValue(formApplicationStart) || undefined,
        application_end:
          parseDateToInputValue(formApplicationEnd) || undefined,
        submission_deadline:
          parseDateToInputValue(formSubmissionDeadline) || undefined,
        support_rate: formSupportRate || undefined,
        budget: formBudget ? formBudget.replace(/\./g, "") : undefined,
        eligible_cities:
          formSelectedCities.length > 0
            ? formSelectedCities.map((c) => c.name).join(", ")
            : undefined,
        eligible_sectors:
          formSelectedSectors.length > 0
            ? formSelectedSectors.map((s) => s.name).join(", ")
            : undefined,
        notes: formNotes || undefined,
        source_url: formSourceUrl || undefined,
      };
      await adminFetch("/call-periods", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      resetForm();
      setShowModal(false);
      refetch();
    } catch {
      // Error shown via UI
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Çağrı ara..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              className="h-9 w-full rounded-lg border border-border bg-secondary pl-9 pr-4 text-sm text-foreground placeholder:text-muted-foreground focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent sm:w-60"
            />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Filter className="h-4 w-4 text-muted-foreground" />
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
              className="h-9 rounded-lg border border-border bg-secondary px-3 text-sm text-foreground focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
            >
              {statusFilterOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <select
              value={programFilter}
              onChange={(e) => {
                setProgramFilter(e.target.value);
                setPage(1);
              }}
              className="h-9 rounded-lg border border-border bg-secondary px-3 text-sm text-foreground focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
            >
              {programFilterOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="inline-flex items-center gap-2 rounded-lg bg-accent px-4 py-2 text-sm font-medium text-accent-foreground transition-colors hover:bg-accent/90"
        >
          <Plus className="h-4 w-4" />
          Yeni Çağrı Ekle
        </button>
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-xl border border-border bg-card">
        {loading ? (
          <PageLoading />
        ) : error ? (
          <ErrorMessage message={error} onRetry={refetch} className="m-4" />
        ) : !data || data.data.length === 0 ? (
          <div className="px-6 py-12 text-center text-sm text-muted-foreground">
            Henüz çağrı dönemi bulunmuyor.
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Başlık
                    </th>
                    <th className="hidden px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground md:table-cell">
                      Kurum
                    </th>
                    <th className="hidden px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground lg:table-cell">
                      Program
                    </th>
                    <th className="hidden px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground xl:table-cell">
                      Çağrı No
                    </th>
                    <th className="hidden px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground lg:table-cell">
                      Başvuru Başlangıç
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Son Tarih
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Durum
                    </th>
                    <th className="hidden px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground sm:table-cell">
                      Proje Sayısı
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {data.data.map((cp) => {
                    const statusInfo = STATUS_CONFIG[cp.status] || {
                      label: cp.status,
                      color: "text-muted-foreground",
                    };
                    const displayInstitution =
                      PROGRAM_INSTITUTIONS[cp.program_type] ||
                      cp.institution ||
                      "-";
                    return (
                      <tr
                        key={cp.id}
                        onClick={() => navigate(`/call-periods/${cp.id}`)}
                        className="cursor-pointer transition-colors hover:bg-secondary/50"
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <Megaphone className="hidden h-4 w-4 shrink-0 text-muted-foreground sm:block" />
                            <span className="text-sm font-medium text-foreground">
                              {cp.title}
                            </span>
                          </div>
                        </td>
                        <td className="hidden px-4 py-3 md:table-cell">
                          <span className="text-sm text-muted-foreground">
                            {displayInstitution}
                          </span>
                        </td>
                        <td className="hidden px-4 py-3 lg:table-cell">
                          <span className="inline-flex items-center rounded-full border border-border px-2.5 py-0.5 text-xs font-medium text-muted-foreground">
                            {PROGRAM_LABELS[cp.program_type] ||
                              cp.program_type}
                          </span>
                        </td>
                        <td className="hidden px-4 py-3 xl:table-cell">
                          <span className="text-sm text-muted-foreground">
                            {cp.call_number || "-"}
                          </span>
                        </td>
                        <td className="hidden px-4 py-3 lg:table-cell">
                          <span className="text-sm text-muted-foreground">
                            {cp.application_start
                              ? formatDateShort(cp.application_start)
                              : "-"}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-col gap-1">
                            <span className="text-sm text-muted-foreground">
                              {cp.submission_deadline
                                ? formatDateShort(cp.submission_deadline)
                                : "-"}
                            </span>
                            {cp.submission_deadline && (
                              <DeadlineBadge
                                deadline={cp.submission_deadline}
                              />
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleStatusCycle(cp.id, cp.status);
                            }}
                            disabled={changingStatusId === cp.id}
                            title={STATUS_CYCLE[cp.status] ? `${statusInfo.label} -> ${STATUS_CONFIG[STATUS_CYCLE[cp.status]]?.label || STATUS_CYCLE[cp.status]}` : undefined}
                            className={cn(
                              "inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-medium transition-opacity hover:opacity-80",
                              statusInfo.color,
                              changingStatusId === cp.id && "cursor-not-allowed opacity-50"
                            )}
                          >
                            {changingStatusId === cp.id && (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            )}
                            {statusInfo.label}
                          </button>
                        </td>
                        <td className="hidden px-4 py-3 sm:table-cell">
                          <span className="text-sm text-muted-foreground">
                            {cp.project_count ?? 0}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <Pagination
              page={data.pagination.page}
              totalPages={data.pagination.totalPages}
              onPageChange={setPage}
            />
          </>
        )}
      </div>

      {/* New Call Period Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/60"
            onClick={() => setShowModal(false)}
          />
          <div className="relative z-10 max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl border border-border/60 bg-card p-6 shadow-2xl ring-1 ring-white/5">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-foreground">
                Yeni Çağrı Dönemi
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="rounded-lg p-1 text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="mt-4 space-y-4">
              {/* Program Type */}
              <div>
                <label className="mb-1.5 block text-sm font-medium text-foreground">
                  Program Türü *
                </label>
                <select
                  required
                  value={formProgramType}
                  onChange={(e) => setFormProgramType(e.target.value)}
                  className="h-9 w-full rounded-lg border border-border bg-secondary px-3 text-sm text-foreground focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
                >
                  <option value="">Seçin...</option>
                  {Object.entries(PROGRAM_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Title */}
              <div>
                <label className="mb-1.5 block text-sm font-medium text-foreground">
                  Başlık *
                </label>
                <input
                  type="text"
                  required
                  value={formTitle}
                  onChange={(e) => setFormTitle(e.target.value)}
                  className="h-9 w-full rounded-lg border border-border bg-secondary px-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
                />
              </div>

              {/* Call Number */}
              <div>
                <label className="mb-1.5 block text-sm font-medium text-foreground">
                  Çağrı Numarası
                </label>
                <input
                  type="text"
                  placeholder='ör. "10" veya "1501 2026/1"'
                  value={formCallNumber}
                  onChange={(e) => setFormCallNumber(e.target.value)}
                  className="h-9 w-full rounded-lg border border-border bg-secondary px-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
                />
              </div>

              {/* Description */}
              <div>
                <label className="mb-1.5 block text-sm font-medium text-foreground">
                  Açıklama
                </label>
                <textarea
                  value={formDescription}
                  onChange={(e) => setFormDescription(e.target.value)}
                  rows={3}
                  className="w-full rounded-lg border border-border bg-secondary px-3 py-2 text-sm text-foreground focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
                />
              </div>

              {/* Date Fields */}
              <DatePickerField
                label="Başvuru Başlangıç"
                value={formApplicationStart}
                onChange={setFormApplicationStart}
              />
              <DatePickerField
                label="Başvuru Bitiş"
                value={formApplicationEnd}
                onChange={setFormApplicationEnd}
              />
              <DatePickerField
                label="Son Teslim Tarihi *"
                value={formSubmissionDeadline}
                onChange={setFormSubmissionDeadline}
                required
              />

              {/* Support Rate + Budget */}
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-foreground">
                    Destek Oranı
                  </label>
                  <select
                    value={formSupportRate}
                    onChange={(e) => setFormSupportRate(e.target.value)}
                    className="h-9 w-full rounded-lg border border-border bg-secondary px-3 text-sm text-foreground focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
                  >
                    <option value="">Seçin...</option>
                    {SUPPORT_RATE_OPTIONS.map((rate) => (
                      <option key={rate} value={rate}>
                        {rate}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-foreground">
                    Bütçe (TL)
                  </label>
                  <input
                    type="text"
                    placeholder="ör. 50.000.000"
                    value={formBudget}
                    onChange={(e) =>
                      setFormBudget(formatBudgetInput(e.target.value))
                    }
                    className="h-9 w-full rounded-lg border border-border bg-secondary px-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
                  />
                </div>
              </div>

              {/* Eligible Cities */}
              <div>
                <label className="mb-1.5 block text-sm font-medium text-foreground">
                  Uygun İller
                </label>
                <MultiSelectTags
                  options={iller}
                  selected={formSelectedCities}
                  onChange={setFormSelectedCities}
                  placeholder="İl ara..."
                  loading={citiesLoading}
                />
              </div>

              {/* Eligible Sectors */}
              <div>
                <label className="mb-1.5 block text-sm font-medium text-foreground">
                  Uygun Sektörler
                </label>
                <MultiSelectTags
                  options={SECTOR_OPTIONS}
                  selected={formSelectedSectors}
                  onChange={setFormSelectedSectors}
                  placeholder="Sektör ara..."
                />
              </div>

              {/* Source URL */}
              <div>
                <label className="mb-1.5 block text-sm font-medium text-foreground">
                  Kaynak URL
                </label>
                <input
                  type="text"
                  value={formSourceUrl}
                  onChange={(e) => setFormSourceUrl(e.target.value)}
                  className="h-9 w-full rounded-lg border border-border bg-secondary px-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
                />
              </div>

              {/* Notes */}
              <div>
                <label className="mb-1.5 block text-sm font-medium text-foreground">
                  Notlar
                </label>
                <textarea
                  value={formNotes}
                  onChange={(e) => setFormNotes(e.target.value)}
                  rows={2}
                  className="w-full rounded-lg border border-border bg-secondary px-3 py-2 text-sm text-foreground focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
                />
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="rounded-lg border border-border px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
                >
                  İptal
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className={cn(
                    "inline-flex items-center gap-2 rounded-lg bg-accent px-4 py-2 text-sm font-medium text-accent-foreground transition-colors hover:bg-accent/90",
                    submitting && "cursor-not-allowed opacity-50"
                  )}
                >
                  {submitting && (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  )}
                  Kaydet
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
